import { useState, useEffect } from 'react';

interface Section {
  id: string;
  title: string;
  content: React.ReactNode;
}

export default function CookiesPage() {
  const [activeSection, setActiveSection] = useState('intro');

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map((s) => ({
        id: s.id,
        element: document.getElementById(s.id),
      }));

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const el = sectionElements[i].element;
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200) {
            setActiveSection(sectionElements[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sections: Section[] = [
    {
      id: 'intro',
      title: '1. Introduction',
      content: (
        <>
          <p className="text-foreground-400 leading-relaxed mb-3">
            Trusted Money Transfer (&ldquo;TMT,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) uses cookies and similar tracking technologies to provide, protect, and improve our Services. This Cookie Policy explains what cookies are, how we use them, the types of cookies we employ, and your choices regarding their use.
          </p>
          <p className="text-foreground-400 leading-relaxed mb-3">
            This policy applies to all visitors and users of the TMT website, mobile application, and any associated services (collectively, the &ldquo;Services&rdquo;). By continuing to use our Services, you consent to the placement and reading of cookies on your device in accordance with this policy, unless you have adjusted your browser settings to refuse cookies.
          </p>
          <p className="text-foreground-400 leading-relaxed">
            For users in the European Economic Area, we obtain your explicit consent before placing any non-essential cookies on your device. You may withdraw or modify your consent at any time through the cookie preference center accessible via the footer of our website.
          </p>
        </>
      ),
    },
    {
      id: 'what-are-cookies',
      title: '2. What Are Cookies?',
      content: (
        <>
          <p className="text-foreground-400 leading-relaxed mb-3">
            Cookies are small text files that are placed on your computer, smartphone, or other device when you visit a website. They are widely used to make websites work more efficiently, as well as to provide information to the owners of the site. Cookies can be &ldquo;session cookies&rdquo; (which are deleted when you close your browser) or &ldquo;persistent cookies&rdquo; (which remain on your device for a set period or until you delete them manually).
          </p>
          <p className="text-foreground-400 leading-relaxed mb-3">
            In addition to cookies, we may use other similar technologies such as web beacons (also known as pixel tags or clear GIFs), local storage, and device fingerprinting. These technologies function similarly to cookies and are used for the same purposes described in this policy.
          </p>
          <p className="text-foreground-400 leading-relaxed">
            Cookies do not typically contain any information that personally identifies a user, but personal information that we store about you may be linked to the information stored in and obtained from cookies.
          </p>
        </>
      ),
    },
    {
      id: 'how-we-use',
      title: '3. How We Use Cookies',
      content: (
        <>
          <p className="text-foreground-400 leading-relaxed mb-3">
            We use cookies for a variety of purposes, which can be grouped into the following categories:
          </p>
          <ul className="space-y-2.5 text-foreground-400 text-sm leading-relaxed mb-3">
            <li className="flex gap-2"><span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span><span><strong className="text-foreground-200">Strictly Necessary Cookies:</strong> These cookies are essential for the operation of our Services. They enable core functionality such as user authentication, session management, security features, and fraud prevention. Without these cookies, the Services cannot be provided. These cookies are placed automatically and do not require consent.</span></li>
            <li className="flex gap-2"><span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span><span><strong className="text-foreground-200">Performance & Analytics Cookies:</strong> These cookies help us understand how visitors interact with our Services by collecting and reporting information anonymously. We use this data to improve website performance, identify and fix errors, and optimize user experience. Data collected includes page load times, navigation paths, feature usage patterns, and error logs.</span></li>
            <li className="flex gap-2"><span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span><span><strong className="text-foreground-200">Functional Cookies:</strong> These cookies enable enhanced functionality and personalization, such as remembering your language preference, display settings, and custom dashboard configurations. They may be set by us or by third-party providers whose services we have integrated into our platform.</span></li>
            <li className="flex gap-2"><span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span><span><strong className="text-foreground-200">Security Cookies:</strong> These cookies are used exclusively for security purposes, including detecting and preventing suspicious login attempts, enforcing multi-factor authentication flows, and protecting against cross-site request forgery (CSRF) attacks.</span></li>
          </ul>
          <p className="text-foreground-400 leading-relaxed">
            We do <strong className="text-foreground-200">not</strong> use advertising cookies, retargeting pixels, or third-party tracking cookies for marketing purposes. Your browsing activity on our platform is never sold to advertisers or data brokers.
          </p>
        </>
      ),
    },
    {
      id: 'types',
      title: '4. Types of Cookies We Use',
      content: (
        <>
          <p className="text-foreground-400 leading-relaxed mb-3">
            Below is a detailed breakdown of the specific cookies used on the TMT platform:
          </p>
          <div className="overflow-x-auto mb-3">
            <table className="w-full text-sm text-left border border-primary-800/20 rounded-lg">
              <thead>
                <tr className="bg-background-100 border-b border-primary-800/20">
                  <th className="px-4 py-3 font-label text-xs uppercase tracking-wider text-foreground-200">Cookie Name</th>
                  <th className="px-4 py-3 font-label text-xs uppercase tracking-wider text-foreground-200">Type</th>
                  <th className="px-4 py-3 font-label text-xs uppercase tracking-wider text-foreground-200">Duration</th>
                  <th className="px-4 py-3 font-label text-xs uppercase tracking-wider text-foreground-200">Purpose</th>
                </tr>
              </thead>
              <tbody className="text-foreground-400">
                <tr className="border-b border-primary-800/10">
                  <td className="px-4 py-3 font-mono text-xs">tmt_session_id</td>
                  <td className="px-4 py-3">Session</td>
                  <td className="px-4 py-3">Session</td>
                  <td className="px-4 py-3">Maintains user session state and authentication status.</td>
                </tr>
                <tr className="border-b border-primary-800/10">
                  <td className="px-4 py-3 font-mono text-xs">tmt_auth_token</td>
                  <td className="px-4 py-3">HttpOnly / Secure</td>
                  <td className="px-4 py-3">7 days</td>
                  <td className="px-4 py-3">Stores encrypted authentication token for persistent login.</td>
                </tr>
                <tr className="border-b border-primary-800/10">
                  <td className="px-4 py-3 font-mono text-xs">tmt_mfa_state</td>
                  <td className="px-4 py-3">Session</td>
                  <td className="px-4 py-3">Session</td>
                  <td className="px-4 py-3">Tracks multi-factor authentication flow state.</td>
                </tr>
                <tr className="border-b border-primary-800/10">
                  <td className="px-4 py-3 font-mono text-xs">tmt_pref_lang</td>
                  <td className="px-4 py-3">Persistent</td>
                  <td className="px-4 py-3">1 year</td>
                  <td className="px-4 py-3">Remembers user language preference (English, Arabic, French).</td>
                </tr>
                <tr className="border-b border-primary-800/10">
                  <td className="px-4 py-3 font-mono text-xs">tmt_pref_theme</td>
                  <td className="px-4 py-3">Persistent</td>
                  <td className="px-4 py-3">1 year</td>
                  <td className="px-4 py-3">Stores light/dark mode preference.</td>
                </tr>
                <tr className="border-b border-primary-800/10">
                  <td className="px-4 py-3 font-mono text-xs">tmt_csrf_token</td>
                  <td className="px-4 py-3">Session</td>
                  <td className="px-4 py-3">Session</td>
                  <td className="px-4 py-3">Protects against cross-site request forgery attacks.</td>
                </tr>
                <tr className="border-b border-primary-800/10">
                  <td className="px-4 py-3 font-mono text-xs">tmt_analytics_id</td>
                  <td className="px-4 py-3">Persistent</td>
                  <td className="px-4 py-3">2 years</td>
                  <td className="px-4 py-3">Anonymous visitor identifier for aggregated analytics.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-xs">tmt_consent_record</td>
                  <td className="px-4 py-3">Persistent</td>
                  <td className="px-4 py-3">1 year</td>
                  <td className="px-4 py-3">Records cookie consent choices to prevent repeated prompts.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-foreground-400 leading-relaxed">
            Additional session cookies may be generated dynamically during active use of the platform for temporary state management. These are automatically cleared when the session ends.
          </p>
        </>
      ),
    },
    {
      id: 'third-party',
      title: '5. Third-Party Cookies',
      content: (
        <>
          <p className="text-foreground-400 leading-relaxed mb-3">
            While TMT does not use advertising or tracking cookies, we do integrate with a limited number of trusted third-party service providers whose technologies may place cookies on your device:
          </p>
          <ul className="space-y-2.5 text-foreground-400 text-sm leading-relaxed mb-3">
            <li className="flex gap-2"><span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span><span><strong className="text-foreground-200">Cloud Infrastructure:</strong> Our hosting provider uses necessary cookies for load balancing, DDoS protection, and secure session routing. These are strictly technical and do not track user behavior.</span></li>
            <li className="flex gap-2"><span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span><span><strong className="text-foreground-200">Payment Processing:</strong> Our payment gateway partners may place security cookies during the checkout process to prevent fraud and ensure PCI-DSS compliance.</span></li>
            <li className="flex gap-2"><span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span><span><strong className="text-foreground-200">Identity Verification:</strong> Our KYC verification partner may use session cookies during the document upload and facial recognition workflow.</span></li>
            <li className="flex gap-2"><span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span><span><strong className="text-foreground-200">Error Monitoring:</strong> Our error tracking service uses anonymized session identifiers to help us diagnose and resolve platform issues quickly.</span></li>
          </ul>
          <p className="text-foreground-400 leading-relaxed">
            We do not permit any third party to use cookies on our platform for purposes outside the scope of the services they provide to us. All third-party providers are contractually bound to process data in accordance with applicable data protection laws and are prohibited from combining data collected through our platform with data from other sources.
          </p>
        </>
      ),
    },
    {
      id: 'manage',
      title: '6. Managing Your Cookie Preferences',
      content: (
        <>
          <p className="text-foreground-400 leading-relaxed mb-3">
            You have the right to control which cookies are placed on your device. Here are the methods available to manage your preferences:
          </p>
          <ul className="space-y-2.5 text-foreground-400 text-sm leading-relaxed mb-3">
            <li className="flex gap-2"><span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span><span><strong className="text-foreground-200">Cookie Preference Center:</strong> European users can access our cookie consent banner upon first visit or through the footer link at any time to modify preferences for non-essential cookies.</span></li>
            <li className="flex gap-2"><span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span><span><strong className="text-foreground-200">Browser Settings:</strong> All major browsers allow you to block or delete cookies through their settings. Please consult your browser&rsquo;s help documentation for specific instructions. Note that disabling necessary cookies will prevent you from accessing or using the Services.</span></li>
            <li className="flex gap-2"><span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span><span><strong className="text-foreground-200">Do Not Track (DNT):</strong> Some browsers support a DNT signal. TMT respects this signal and will disable non-essential analytics cookies when DNT is enabled.</span></li>
            <li className="flex gap-2"><span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span><span><strong className="text-foreground-200">Mobile App Settings:</strong> Within the TMT mobile application, you can manage analytics and functional cookie preferences through Settings &gt; Privacy &gt; Cookie Preferences.</span></li>
          </ul>
          <p className="text-foreground-400 leading-relaxed">
            Please be aware that restricting cookies may impact the functionality and user experience of our Services. Necessary cookies cannot be disabled while using the platform, as they are required for security and core operational features.
          </p>
        </>
      ),
    },
    {
      id: 'retention',
      title: '7. Data Retention',
      content: (
        <p className="text-foreground-400 leading-relaxed">
          Session cookies are automatically deleted when you close your browser. Persistent cookies remain on your device for the duration specified in the table above (ranging from 7 days to 2 years), unless you delete them earlier through your browser settings. Cookie consent records are retained for one year to prevent repeated consent prompts. Analytics data derived from cookies is aggregated and anonymized within 90 days, after which individual cookie identifiers are no longer associated with browsing behavior.
        </p>
      ),
    },
    {
      id: 'changes',
      title: '8. Changes to This Policy',
      content: (
        <p className="text-foreground-400 leading-relaxed">
          We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or our data practices. Material changes will be communicated via an in-app notification and email alert at least thirty (30) days before they take effect. The &ldquo;Last Updated&rdquo; date at the top of this page indicates when the policy was last revised. We encourage you to review this policy periodically to stay informed about how we use cookies.
        </p>
      ),
    },
    {
      id: 'contact',
      title: '9. Contact Us',
      content: (
        <>
          <p className="text-foreground-400 leading-relaxed mb-3">
            If you have any questions about our use of cookies or this Cookie Policy, please contact us:
          </p>
          <div className="bg-background-100 border border-primary-800/20 rounded-xl p-5 space-y-2">
            <p className="text-foreground-200 text-sm"><strong className="text-foreground-100">Email:</strong> <a href="mailto:dpo@trustedmoneytransfer.com" className="text-primary-400 hover:text-primary-300 transition-colors">dpo@trustedmoneytransfer.com</a></p>
            <p className="text-foreground-200 text-sm"><strong className="text-foreground-100">Address:</strong> Data Protection Officer, Trusted Money Transfer, Beirut Digital District 1280, Beirut, Lebanon</p>
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="bg-background-50">
      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <div className="w-full lg:w-3/5">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
              / Legal
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-foreground-50 leading-tight mb-4">
              Cookie <span className="text-primary-400">Policy</span>
            </h1>
            <p className="text-foreground-500 text-sm font-body">
              Last updated: July 20, 2026 &middot; Effective date: August 1, 2026
            </p>
            </div>
            <div className="w-full lg:w-2/5 flex justify-center lg:justify-end">
              <div className="relative w-full max-w-sm aspect-square">
                <div className="absolute inset-0 bg-gradient-to-br from-primary-700/20 via-transparent to-primary-900/20 rounded-full blur-3xl" />
                <img
                  src="https://readdy.ai/api/search-image?query=Luxury%20dark%20abstract%20digital%20browser%20privacy%20concept%20with%20a%20glowing%20golden%20cookie%20icon%20and%20circuit%20node%20patterns%20floating%20over%20deep%20black%20background%2C%20warm%20golden%20data%20flow%20lines%20radiating%20outward%2C%20metallic%20geometric%20network%20patterns%2C%20premium%20elegant%20digital%20consent%20visualization%2C%20minimal%20abstract%20composition%2C%20high-end%20fintech%20aesthetic%2C%20warm%20gold%20on%20charcoal%20black%20palette%2C%20no%20text%20no%20faces&width=800&height=800&seq=cookies-hero&orientation=squarish"
                  alt="Cookie Policy and Digital Consent"
                  className="relative w-full h-full object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content with sidebar */}
      <section className="pb-16 md:pb-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-10 lg:gap-12">
            {/* Sidebar Navigation */}
            <aside className="hidden lg:block lg:col-span-1">
              <div className="sticky top-28">
                <h3 className="font-label text-xs tracking-wider uppercase text-foreground-300 mb-4">
                  Contents
                </h3>
                <nav className="space-y-1 border-l border-primary-800/20">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`block w-full text-left pl-4 py-1.5 text-sm font-body transition-colors border-l-2 ${
                        activeSection === section.id
                          ? 'text-primary-400 border-primary-400'
                          : 'text-foreground-400 border-transparent hover:text-foreground-200'
                      }`}
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-10">
              {sections.map((section) => (
                <div key={section.id} id={section.id}>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 mb-4">
                    {section.title}
                  </h2>
                  <div className="text-sm md:text-base font-body">
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}